/**
 * Focus reflow command and its triggers: a window resize, the end of a
 * native geometry transition, and a card-only frame's detail landing. The
 * layout itself comes from `lib/focus/focus-plan-reflow.ts`.
 */
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useFramesStore } from "@/stores/frames-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { calculateContentAwareFrameBox } from "@/lib/canvas-presentation-layout";
import {
  currentFramePresentationSurface,
  localFramePresentationExclusions,
} from "@/desktop/frame-presentation-surface";
import { commitPresentation, focusAdapterFor } from "@/lib/focus/focus-adapter";
import {
  currentFocusPlanEnv,
  landedNaturalSizes,
} from "@/lib/focus/focus-natural-size";
import {
  planFocusReflow,
  planNaturalSizeRefresh,
  reflowChangesNothing,
} from "@/lib/focus/focus-plan-reflow";
import type { PresentationState } from "@/lib/focus/focus-types";
import type { FrameData } from "@/lib/frame-data";

let reflowTimer: ReturnType<typeof setTimeout> | null = null;
interface FocusPresentationReflowOptions {
  duration?: number;
}

/** Mirrored windows share the presentation; only its display lays it out. */
function laysOutHere(displayId: number | null | undefined): boolean {
  const windowDisplayId = getWindowDisplayId();
  return displayId == null || windowDisplayId == null
    ? true
    : displayId === windowDisplayId;
}

/** The box an agent's content fit asks for in the current surface. */
export function agentContentFitBox(presentation: PresentationState) {
  const fit = presentation.agentContentFit;
  const activeId = presentation.frameIds[presentation.activeIndex];
  if (!fit || presentation.frameIds.length !== 1 || fit.frameId !== activeId) {
    return undefined;
  }
  return calculateContentAwareFrameBox({
    surface: currentFramePresentationSurface(fit.fitToDisplay),
    desired: fit.desired,
    exclusions: localFramePresentationExclusions(fit.exclusions),
    placement: fit.placement,
  });
}

function commitReflow(
  presentation: PresentationState,
  next: PresentationState,
  duration = 0,
): void {
  const options = { duration, reflow: true };
  if (reflowChangesNothing(presentation, next)) {
    focusAdapterFor(next.renderer).apply(presentation, next, options);
    return;
  }
  commitPresentation(next, options, presentation);
}

export function reflowFocusPresentationLayout(
  options: FocusPresentationReflowOptions = {},
): boolean {
  const presentation = usePresentationStore.getState().presentation;
  if (presentation?.variant !== "focus") return false;
  // Fitting a background display would resize the frame on the display
  // that actually presented it.
  if (!laysOutHere(presentation.agentContentFit?.targetDisplayId)) {
    return false;
  }
  const next = planFocusReflow(
    presentation,
    { contentFitBox: agentContentFitBox(presentation) },
    currentFocusPlanEnv(),
  );
  if (!next) return false;
  commitReflow(presentation, next, options.duration);
  return true;
}

export function scheduleFocusPresentationReflow(
  delay = 0,
  options: FocusPresentationReflowOptions = {},
): void {
  if (reflowTimer !== null) clearTimeout(reflowTimer);
  reflowTimer = setTimeout(() => {
    reflowTimer = null;
    if (useDesktopMode.getState().nativeGeometryTransitioning) return;
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => reflowFocusPresentationLayout(options));
      return;
    }
    reflowFocusPresentationLayout(options);
  }, delay);
}

/** A card-only frame's size is refreshed once, when its detail lands. */
function refreshLandedNaturalSizes(
  framesById: Readonly<Record<string, FrameData>>,
  previousFramesById: Readonly<Record<string, FrameData>>,
): void {
  const presentation = usePresentationStore.getState().presentation;
  if (
    presentation?.variant !== "focus" ||
    !laysOutHere(presentation.displayId)
  ) {
    return;
  }
  const landed = landedNaturalSizes(
    presentation,
    framesById,
    previousFramesById,
  );
  if (!landed) return;
  const next = planNaturalSizeRefresh(
    presentation,
    landed,
    currentFocusPlanEnv(),
  );
  commitReflow(presentation, next);
}

const unsubscribeDesktopReflow =
  typeof useDesktopMode.subscribe === "function"
    ? useDesktopMode.subscribe((state, previous) => {
        if (
          previous.nativeGeometryTransitioning &&
          !state.nativeGeometryTransitioning
        ) {
          scheduleFocusPresentationReflow();
        }
      })
    : () => {};

const unsubscribeDetailRefresh =
  typeof useFramesStore.subscribe === "function"
    ? useFramesStore.subscribe((state, previous) => {
        if (state.framesById !== previous.framesById) {
          refreshLandedNaturalSizes(state.framesById, previous.framesById);
        }
      })
    : () => {};

function handleFocusPresentationResize() {
  if (usePresentationStore.getState().presentation?.variant !== "focus") return;
  scheduleFocusPresentationReflow(80);
}

if (typeof window !== "undefined") {
  window.addEventListener("resize", handleFocusPresentationResize);
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribeDesktopReflow();
    unsubscribeDetailRefresh();
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", handleFocusPresentationResize);
    }
    if (reflowTimer !== null) clearTimeout(reflowTimer);
    reflowTimer = null;
  });
}
